// Wraps a plain page in the app rail.
//
// The Studio builds its own <AppShell> because its rail switches screens rather
// than routes. Every other signed-in page just needs the standard nav, which is
// what this provides — so the frame is identical wherever you are, instead of
// the chrome changing shape between the editor and the rest of the app.
import { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AppShell, RailGroup } from './AppShell';

// Same glyphs the Studio's rail uses, so an item doesn't change shape when you
// navigate onto or off the Studio.
const ICON = {
  // Panelled square: a summary board, distinct from the arrow/note/loop glyphs
  // the working pages use.
  dashboard: 'M4 5.5A1.5 1.5 0 015.5 4h13A1.5 1.5 0 0120 5.5v13a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 18.5zM4 10h16M10 10v10',
  separator: 'M4 12h10M10 7l5 5-5 5M17 5v14',
  songs: 'M9 18V6l10-2v12M6 20.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM16 18.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  loops: 'M17 2l4 4-4 4M3 11V9a4 4 0 014-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 01-4 4H3',
};

export function RailPage({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const groups: RailGroup[] = [
    // Headingless: the dashboard is the way back to the top of the app, not a
    // product, so it sits above the first section rather than inside one. The
    // brand mark already links here — this makes that reachable rather than
    // something you have to guess.
    {
      label: '',
      items: [
        { id: 'dashboard', icon: ICON.dashboard, label: 'Dashboard', to: '/dashboard', active: pathname === '/dashboard' },
      ],
    },
    {
      label: 'Products',
      items: [
        { id: 'setup', icon: ICON.separator, label: 'Stem Separator', to: '/', active: pathname === '/' },
      ],
    },
    {
      label: 'Library',
      items: [
        { id: 'songs', icon: ICON.songs, label: 'Your songs', to: '/songs', active: pathname === '/songs' },
        { id: 'loops', icon: ICON.loops, label: 'Saved loops', to: '/sections', active: pathname === '/sections' },
      ],
    },
  ];
  return (
    <AppShell groups={groups}>
      {/* Pages were written to sit under the old TopBar, i.e. against the page
          background with their own padding. `sxm-body` gives them the rail's
          gutter and makes the column the scroller, matching the Studio. */}
      <div className="sxm-body">{children}</div>
    </AppShell>
  );
}
